import { Pipe, PipeTransform, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * translates keys to platformaware, pluralized translations in <lang>.json
 * if the key "transkey" does not exist, there's a second look for "transkey.AR"
 * if the param "pluralize" exists and is a number, the transkey will be pluralized to transkeyNone, transkeySingle, transkeyMultiple
 * or
 * transkeyNone.AR, transkeySingle.AR, transkeyMultiple.AR
 * for example:
 * 'LAYOUT.transkey'|alpitranslate:{pluralize: 0} -> LAYOUT.transkeyNone / LAYOUT.AR.transkeyNone
 */
@Pipe({
  name: 'alpitranslate',
  pure: false
})
export class AlpitranslatePipe implements PipeTransform, OnDestroy {
  protected transVal: string; // finished translation

  private tranSub: Subscription;

  constructor(
    private translate: TranslateService,
    private platformservice: PlatformInformationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy() {
    this.tranSub?.unsubscribe();
  }

  /**
   * will translate to a pluralized, platformspecific transkey with
   * variable placeholders in params to return a translation from the
   * de/en/...json files
   * @param transKey your transkey
   * @param params your params for the translation.
   * 'pluralate' will be used to select a pluralated translation between none (0), single (1) or multiple (1+)
   */
  transform(transKey: string, params?: { [key: string]: string | number }) {
    if (params && params.pluralate !== undefined) {
      transKey = this.pluralateTranskey(transKey, +params.pluralate);
    }
    this.updateTranslation(transKey, params);
    return this.transVal;
  }

  updateTranslation(key: string, params?: object) {
    this.tranSub?.unsubscribe();
    this.tranSub = this.translate.get(key, params).subscribe(answer => {
      if (answer !== key && answer !== null) {
        // translation is not key (we have a real translation)
        this.transVal = answer;
        this.cdr.markForCheck();
      } else {
        // insert platform shortcode into string at second postion
        // eg ALPINSKIHIRE.discountPromotionHeader ->
        // ALPINSKIHRE.AR.discountPromotionHeader
        const keyParts = key.split('.');
        keyParts.splice(1, 0, this.platformservice.getCurrentPlatformShortcode().toUpperCase());
        const platformKey = keyParts.join('.');

        this.translate.get(platformKey, params).subscribe(result => {
          if (result === platformKey) {
            // TODO: we'd need to add a resolver to our component renders before
            // the warning would be helpful
            // console.warn(`could not find translation for [${platformKey}]`);
            result = '';
          }
          this.transVal = result;
          this.cdr.markForCheck();
        });
      }
    });
  }

  private pluralateTranskey(transKey: string, count: number) {
    if (+count === 0) {
      return transKey + 'None';
    } else if (+count === 1) {
      return transKey + 'Single';
    } else if (+count > 1) {
      return transKey + 'Multiple';
    }
    return transKey;
  }
}
